import Tooltip from '@/components/Tooltip';
import { Term, TooltipTitle } from './styled';
import { useTracking } from 'react-tracking';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { RouterLink } from '@redux-first-router-link';
import type { Statement } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import type { SummaryRiskStatement } from '@/pages/CompanyReport/components/OverviewSection/types';
import { useCallback } from 'react';
import {
  useStatementTranslation,
  termIsValid,
} from '@/hooks/useTranslation/useStatementTranslation';

//TODO @ric: remove ReduxStatement type once Redux statements is completely removed from the Report
interface ReduxStatement {
  description: string;
  context: Record<string, string>;
  question: string;
  url?: { href: string; label: string } | null;
}

type StatementProp =
  | SummaryRiskStatement
  | Statement
  | ReduxStatement
  | undefined;

interface Props {
  statement?: StatementProp;
  showTooltipQuestion?: boolean;
  zIndex?: number;
}

const toMatch = /\[\[(.*?)\]\]/g;
const openDelay = 300;

function propIsStatement(statement: StatementProp): statement is Statement {
  return typeof statement !== 'undefined' && '_tooltip_' in statement;
}
const Description = ({ statement, showTooltipQuestion, zIndex }: Props) => {
  const getTerm = useGlossaryTerms();
  const tracking = useTracking();

  const t = useStatementTranslation();

  const getGlossaryWithTooltip = useCallback(
    (term: string, index: number, trackCallback: () => void) => {
      /** Glossary tooltip */
      let matchedGlossary = getTerm(term.toLowerCase());

      if (propIsStatement(statement) && matchedGlossary === null) {
        Object.entries(statement._tooltip_ || {}).forEach(([key, value]) => {
          const safeKey = key.toLowerCase();
          if (value === term && termIsValid(safeKey)) {
            matchedGlossary = t(safeKey);
          }
        });
      }
      return (
        <Tooltip
          key={index}
          title={<TooltipTitle>{matchedGlossary}</TooltipTitle>}
          onOpen={trackCallback}
          openDelay={openDelay}
          light
          zIndex={zIndex}
        >
          <Term>{term}</Term>
        </Tooltip>
      );
    },
    [getTerm, zIndex, statement, t]
  );

  if (statement === undefined) return null;
  const { description, context, question, url } = statement;
  if (description === null) {
    return <></>;
  }
  /** At this point we made sure description IS a string */

  const statementQuestion =
    typeof question === 'string' ? question : question?.data[0] || '';
  const result = showTooltipQuestion
    ? statementQuestion.split(toMatch)
    : description.split(toMatch);

  return (
    <>
      {result.map((term, index) => {
        /** Haha so dodge.  */
        if (index % 2) {
          const trackCallback = () => {
            tracking.trackEvent({
              action: 'hover',
              modifier: 'jargon',
              properties: term,
            });
          };
          if (context && Object.keys(context).length > 0) {
            const hasContext = context.hasOwnProperty(term);
            if (hasContext) {
              /** Context tooltip */
              const tooltipTitle = context[term];
              return (
                <Tooltip
                  key={index}
                  title={<TooltipTitle>{tooltipTitle}</TooltipTitle>}
                  onOpen={trackCallback}
                  openDelay={openDelay}
                  light
                  zIndex={zIndex}
                >
                  <Term>{term}</Term>
                </Tooltip>
              );
            }
            /** For statements with context AND glossary terms */
            return getGlossaryWithTooltip(term, index, trackCallback);
          } else {
            /** For statements with only glossary terms */
            return getGlossaryWithTooltip(term, index, trackCallback);
          }
        }
        if (url) {
          const { label, href } = url;
          const dividedPieces = description.split(label);
          if (dividedPieces.length === 2) {
            return (
              <span key={`span-${index}`}>
                {dividedPieces[0]} <RouterLink to={href}>{label}</RouterLink>
                {dividedPieces[1]}
              </span>
            );
          }
        }
        return <span key={`span-${index}`}>{term}</span>;
      })}
    </>
  );
};

export default Description;
